/**
 * Gifting is always a one-time purchase.
 *
 * When the shopper opens the Giftnote flow on a product page, any selected subscription selling
 * plan is switched to one-time, and a notice explaining that is shown inside the Giftnote modal.
 *
 * How the gift flow is detected
 * -----------------------------
 * Giftnote dispatches `giftnote_open` on `document` when its modal opens (verified in the app
 * bundle and live: `document.dispatchEvent(new CustomEvent('giftnote_open', { detail: { appId } }))`).
 * That is the trigger — no click handler is bound to the app's button, and the app itself is not
 * modified.
 *
 * How one-time is enforced
 * ------------------------
 * The source of truth is the product form's `selling_plan` field, which is what
 * assets/product-form.js reads (`new FormData(form).get('selling_plan')`). Rather than writing to
 * that field directly, this clicks the subscription widget's own "one-time purchase" control so the
 * widget's handlers run and the visible UI, the price and the hidden field all update together.
 * The field is then re-read, and only if the widget did not clear it does this fall back to
 * clearing the field by hand.
 *
 * Scope
 * -----
 * Only the product-page gift flow, on pages with a product form that has a `selling_plan` control.
 * Gifting from a cart line is identified by the clicked button's placement and left alone, so cart
 * gifting is untouched. Subscriptions are never disabled — normal (non-gift) subscription purchases
 * are unaffected, because nothing runs until the gift modal is opened.
 *
 * Note on the app's own behaviour
 * -------------------------------
 * As of 2026-10-05 Giftnote already clears the selling plan itself, before `giftnote_open` fires:
 * the plan reads `3761635493` immediately before the click and `""` by the time the event lands.
 * The switching below is therefore a verified no-op on the current app version, kept as a safety
 * net because that behaviour is undocumented and could change. It only acts when a plan is still
 * set, so it cannot fight the app.
 */

const NOTICE_TEXT =
  'Subscription as a gift is not available. This gift will be sent as a one-time purchase.';
const NOTICE_ID = 'yc-giftnote-one-time-notice';

const NOTICE_STYLES = `
  #${NOTICE_ID} {
    margin: 0 0 1rem;
    padding: var(--padding-xs, 0.5rem) var(--padding-sm, 0.7rem);
    border-radius: var(--style-border-radius-buttons-secondary, 6px);
    background-color: rgb(from var(--color-success, #006400) r g b / 0.1);
    color: var(--color-success, #006400);
    font-size: var(--font-size--sm, 0.875rem);
    line-height: var(--font-paragraph--line-height, 1.4);
    text-align: center;
  }
`;

/* ------------------------------------------------------------------ product form */

/** @returns {HTMLFormElement | null} the product form, if it offers selling plans */
function getProductForm() {
  for (const form of document.querySelectorAll('form[action*="/cart/add"]')) {
    if (form.querySelector('[name="selling_plan"]')) return form;
  }
  return null;
}

/**
 * Reads the value Shopify would actually receive.
 * @param {HTMLFormElement} form
 */
function getSellingPlan(form) {
  const value = new FormData(form).get('selling_plan');
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Clicks the subscription widget's own one-time control so its handlers run.
 * @param {HTMLFormElement} form
 * @returns {string | null} which widget was driven, or null if none was recognised
 */
function selectOneTimeControl(form) {
  // Loop Subscriptions widget (snippets/loop-widget-standard.liquid)
  const loopOneTime = form.querySelector(
    'input[name="loop_purchase_option"][data-name="loop-one-time-purchase"]'
  );
  if (loopOneTime) {
    if (!loopOneTime.checked) loopOneTime.click();
    return 'loop';
  }

  // Theme buy box (sections/yc-buy-box.liquid), used on templates that do not load Loop
  const buyBoxOneTime = form.querySelector('[data-bb-order="onetime"]');
  if (buyBoxOneTime) {
    if (!buyBoxOneTime.checked) {
      buyBoxOneTime.checked = true;
      buyBoxOneTime.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return 'buy-box';
  }

  return null;
}

/**
 * Last resort when no widget control was found or the widget left a plan in place.
 * @param {HTMLFormElement} form
 */
function clearSellingPlanField(form) {
  for (const field of form.querySelectorAll('[name="selling_plan"]')) {
    if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
      field.value = '';
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

/** Switches the product form to a one-time purchase, verifying the result. */
function forceOneTimePurchase() {
  const form = getProductForm();
  if (!form || !getSellingPlan(form)) return;

  selectOneTimeControl(form);

  // The widget updates asynchronously; verify rather than assume, and only touch the field
  // directly if its own handling did not take.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (getSellingPlan(form)) clearSellingPlanField(form);
    });
  });
}

/* ----------------------------------------------------------------------- notice */

/**
 * The modal is injected by the app into <giftnote-core>'s open shadow root, so it cannot be
 * resolved from a static reference at page load.
 * @returns {Element | null} the modal description, which the notice is placed after
 */
function getModalAnchor() {
  const core = document.querySelector('giftnote-core');
  return core?.shadowRoot?.querySelector('#giftnote-modal-description') ?? null;
}

/**
 * Theme CSS cannot select into the shadow root, but custom properties do inherit across the
 * boundary — so the adopted sheet below resolves the theme's own tokens.
 * @param {ShadowRoot} root
 */
function adoptNoticeStyles(root) {
  if (root.querySelector(`[data-${NOTICE_ID}-styles]`)) return;
  const style = document.createElement('style');
  style.setAttribute(`data-${NOTICE_ID}-styles`, '');
  style.textContent = NOTICE_STYLES;
  root.appendChild(style);
}

/** @returns {boolean} whether the notice is in place */
function showNotice() {
  const anchor = getModalAnchor();
  if (!anchor) return false;

  const root = anchor.getRootNode();
  if (!(root instanceof ShadowRoot)) return false;
  if (root.querySelector(`#${NOTICE_ID}`)) return true;

  adoptNoticeStyles(root);

  const notice = document.createElement('p');
  notice.id = NOTICE_ID;
  notice.setAttribute('role', 'status');
  notice.textContent = NOTICE_TEXT;
  anchor.after(notice);
  return true;
}

/** The modal can lag the event, so retry over a short window rather than once. */
function showNoticeWhenReady(attemptsLeft = 20) {
  if (showNotice() || attemptsLeft <= 0) return;
  requestAnimationFrame(() => showNoticeWhenReady(attemptsLeft - 1));
}

/* ---------------------------------------------------------------------- trigger */

/**
 * Which Giftnote button opened the flow. `giftnote_open` carries only an appId, and a product page
 * with the cart drawer open has both kinds of button on it — so without this, gifting from a cart
 * line would wrongly rewrite the product form behind the drawer and show the notice.
 *
 * Clicks inside the button's shadow root are composed, so they retarget to the <giftnote-button>
 * host and reach this listener with data-gn-placement intact (verified live).
 */
let lastPlacement = null;

document.addEventListener(
  'click',
  (event) => {
    const host = event.target instanceof Element ? event.target.closest('giftnote-button') : null;
    if (host) lastPlacement = host.dataset.gnPlacement ?? '';
  },
  true
);

/** Unknown placements are treated as product-page, so this keeps working if the app renames 'pdp'. */
function openedFromCart() {
  return typeof lastPlacement === 'string' && lastPlacement.includes('cart');
}

document.addEventListener('giftnote_open', () => {
  if (openedFromCart()) return;

  // Nothing to switch or explain where there is no subscription choice.
  if (!getProductForm()) return;

  forceOneTimePurchase();
  showNoticeWhenReady();
});
